import { Menu as MenuIcon } from '@mui/icons-material'
import {
  AppBar,
  Avatar,
  Box,
  Button,
  CssBaseline,
  IconButton,
  Menu,
  MenuItem,
  Stack,
  Toolbar,
  Typography,
  useMediaQuery
} from '@mui/material'
import { FC, MouseEvent, useContext, useEffect, useState } from 'react'
import { NavLink, Outlet, useNavigate } from 'react-router-dom'

import { AUTH_TOKEN, DrawerWidthCollapsed, HELMER_KEYS, ROOT_ROUTE, USER_EMAIL } from '../../constants'
import { AppContext } from '../../context'
import { AuthContext } from '../../context/AuthContext'
import { getIsTimerRunning, getStoredToken, handleLogout, stringAvatar, titleize } from '../../utils'
import DesktopDrawer from './DesktopDrawer'
import Timer from '../entries/Timer'
import { useTimerControls } from '../../context/TimerContext'
import { useVersionCheck } from '../../hooks/useVersionCheck'

export const Header: FC = () => {
  const [sidebarOpen, setSidebarOpen] = useState(true)
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)

  const { setIsLoggedIn, setUser, isLoading, currentUser, isAccountant, isAdmin, isSuperAdmin } =
    useContext(AuthContext)
  const { outlookSignOut, outlookUser } = useContext(AppContext)
  const { pauseTimer } = useTimerControls()
  const { currentVersion } = useVersionCheck()

  const { firstName, lastName, roles } = currentUser || {}
  const fullName = `${firstName || ''} ${lastName || ''}`
  const roleName = roles && roles[0]?.role
  const open = Boolean(anchorEl)
  const navigate = useNavigate()
  // The stored token, expired or not: this only reacts to the token being removed.
  // Expiry is SessionWatcher's, which also keeps the page to return to.
  const token = getStoredToken()
  const mediumViewport = useMediaQuery('(min-width:929px)')
  const showAccountingLinks = isAccountant || isAdmin || isSuperAdmin

  const logout = () => {
    if (getIsTimerRunning()) {
      const entryId = JSON.parse(localStorage.getItem(HELMER_KEYS.FORM) || '{}')?.entryId
      pauseTimer(entryId)
    }
    outlookUser && outlookSignOut?.()
    localStorage.removeItem(AUTH_TOKEN)
    localStorage.removeItem(USER_EMAIL)
    setUser(null)
    setIsLoggedIn(false)
    navigate(ROOT_ROUTE)
    handleLogout()
  }

  useEffect(() => {
    if (!token) logout()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token])

  useEffect(() => {
    if (!mediumViewport) {
      setSidebarOpen(false)
    }
  }, [mediumViewport])

  if (isLoading || !currentUser) return null

  const handleClick = (event: MouseEvent<HTMLButtonElement>) => setAnchorEl(event.currentTarget)
  const handleClose = () => setAnchorEl(null)

  const toggleSidebar = () => setSidebarOpen(prev => !prev)
  const headerWidth = 240

  return (
    <Box sx={{ display: 'flex' }}>
      <CssBaseline />

      <AppBar
        position="fixed"
        sx={{
          zIndex: theme => theme.zIndex.drawer + 1,
          boxShadow: 'none',
          left: sidebarOpen ? 268 : 72,
          backgroundColor: '#fff',
          color: '#000',
          width: `calc(100% - ${sidebarOpen ? headerWidth : DrawerWidthCollapsed}px)`
        }}
      >
        <Toolbar sx={{ justifyContent: 'space-between', px: '8px !important' }}>
          <IconButton onClick={toggleSidebar} edge="start" sx={{ marginLeft: 2 }}>
            <MenuIcon />
          </IconButton>

          {showAccountingLinks && (
            // Hidden below md (767px): on phones they crowded the header and pushed it off-screen
            <Box display={{ xs: 'none', md: 'flex' }} alignItems="center" gap={1}>
              <Button component={NavLink} to="/accounts/deposit-fund" size="small" variant="text">
                Deposit Funds
              </Button>
              <Button component={NavLink} to="/accounts/payable" size="small" variant="text">
                Account Payables
              </Button>
              <Button component={NavLink} to="/entries/dashboard" size="small" variant="text">
                All Entries
              </Button>
            </Box>
          )}

          <Box display={'flex'} alignItems={'center'} gap={1} pr={2}>
            {/* Clock is still being rolled out, so only admins get the button.
                `isAdmin` already covers super admins (see AuthContext). The
                provider stays mounted for everyone, so a timer a user already
                has running is not orphaned — it just has no control here. */}
            {isAdmin && <Timer />}

            <Button
              id="basic-button"
              aria-controls={open ? 'header-menu' : undefined}
              aria-haspopup="true"
              aria-expanded={open ? 'true' : undefined}
              onClick={handleClick}
              aria-label="user info"
            >
              <Stack direction="row" spacing="15px" alignItems="center">
                <Avatar {...stringAvatar(fullName)} />
                <Box fontSize="16px" minWidth="100px" display={mediumViewport ? 'block' : 'none'}>
                  <Typography textAlign="left" textTransform="capitalize" color="black" fontSize="16px">
                    {fullName || ''}
                  </Typography>

                  <Typography textAlign="left" textTransform="capitalize" color="gray">
                    {titleize(roleName || '')}
                  </Typography>
                </Box>
              </Stack>
            </Button>

            <Menu
              id="header-menu"
              anchorEl={anchorEl}
              open={open}
              onClose={handleClose}
              MenuListProps={{
                'aria-labelledby': 'basic-button'
              }}
              PaperProps={{
                sx: { minWidth: '200px', marginTop: '15px' }
              }}
            >
              <MenuItem component={NavLink} to="/profile" onClick={handleClose}>
                Profile
              </MenuItem>
              <MenuItem onClick={logout}>Logout</MenuItem>
            </Menu>
          </Box>
        </Toolbar>
      </AppBar>

      <DesktopDrawer sidebarOpen={sidebarOpen} version={currentVersion} />

      <Box
        component="main"
        sx={{
          flexGrow: 1,
          // A flex item defaults to min-width: auto, so a wide table would stretch the whole page
          // instead of scrolling inside its own container.
          minWidth: 0,
          p: 2,
          transition: 'width 0.35s ease-in-out'
        }}
      >
        <Toolbar />
        <Outlet />
      </Box>
    </Box>
  )
}
